import { translate } from "@/i18n";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";
import type en from "@/i18n/messages/lib-errors/en";

export interface ErrorKindLabel {
  title: string;
  description: string;
  level: "info" | "warning" | "error";
}

type Key = keyof typeof en;
const tr = (
  key: Exclude<
    Key,
    "turnCancelledBy" | "wentWrongIn" | "couldNotReach" | "uploadFailedStatus"
  >,
) => translate(libErrorsMessages, key);

/** Each known kind's title and description keys, and how serious it is. */
const KIND_LABELS: Readonly<
  Record<
    string,
    readonly [
      Key & `${string}Title`,
      Key & `${string}Description`,
      ErrorKindLabel["level"],
    ]
  >
> = {
  auth_required: ["authRequiredTitle", "authRequiredDescription", "error"],
  rate_limit: ["rateLimitTitle", "rateLimitDescription", "warning"],
  payment_required: [
    "paymentRequiredTitle",
    "paymentRequiredDescription",
    "error",
  ],
  model_invalid: ["modelInvalidTitle", "modelInvalidDescription", "error"],
  provider_missing: [
    "providerMissingTitle",
    "providerMissingDescription",
    "error",
  ],
  plan_tier: ["planTierTitle", "planTierDescription", "warning"],
  timeout: ["timeoutTitle", "timeoutDescription", "warning"],
};

/** The toast for a failed run's error kind, in the app language. A message
 *  that names an overloaded provider reads as a rate limit whatever its kind. */
export function getErrorKindLabel(
  errorKind: string,
  errMsg: string,
): ErrorKindLabel | null {
  const known = KIND_LABELS[errorKind];
  if (known) {
    const [title, description, level] = known;
    return { title: tr(title), description: tr(description), level };
  }
  const lower = errMsg.toLowerCase();
  if (
    lower.includes("overloaded") ||
    lower.includes("529") ||
    lower.includes("rate limit")
  ) {
    return {
      title: tr("overloadedTitle"),
      description: tr("overloadedDescription"),
      level: "warning",
    };
  }
  return null;
}

/** Plain words for a failure in a list row (search hub §5): a title, an optional short cause, and whether it failed. */
export interface ErrorKindWords {
  title: string;
  cause: string | null;
  failed: boolean;
}

const TOOL_NAME = /\b(Claude Code|Codex|OpenCode)\b/;

export function getErrorKindWords({
  errorKind,
  title,
  message,
  where,
}: {
  errorKind: string;
  /** The stored title; for sign-in failures the backend already names the tool. */
  title: string;
  message: string;
  /** The chat or task it happened in. */
  where: string | null;
}): ErrorKindWords {
  if (errorKind === "cancelled") {
    const tool = TOOL_NAME.exec(message)?.[1];
    return {
      title: tool
        ? translate(libErrorsMessages, "turnCancelledBy", { tool })
        : tr("turnCancelled"),
      cause: null,
      failed: false,
    };
  }
  // Kinds only list rows name; toasts keep their own copy above.
  if (errorKind === "runtime_identity_collision")
    return {
      title: tr("identityCollisionTitle"),
      cause: tr("identityCollisionCause"),
      failed: true,
    };
  const label = getErrorKindLabel(errorKind, message);
  if (label) {
    return {
      title: errorKind === "auth_required" && title ? title : label.title,
      cause: null,
      failed: label.level === "error",
    };
  }
  return {
    title: where
      ? translate(libErrorsMessages, "wentWrongIn", { where })
      : tr("wentWrong"),
    cause: null,
    failed: true,
  };
}
